if (!customElements.get('sticky-add-to-cart-bar')) {
  customElements.define(
    'sticky-add-to-cart-bar',
    class StickyAddToCartBar extends HTMLElement {
      constructor() {
        super();

        this.onScroll = this.updateVisibility.bind(this);
        this.onResize = this.handleResize.bind(this);
        this.onChange = this.handleChange.bind(this);
        this.onVariantChange = this.handleVariantChange.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.productArea = document.querySelector(this.dataset.productAreaSelector);
        this.addEventListener('change', this.onChange);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.handleResize();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        this.variantChangeUnsubscriber?.();
        this.setVisible(false);
      }

      handleResize() {
        window.requestAnimationFrame(() => {
          this.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
          document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
          this.updateVisibility();
        });
      }

      updateVisibility() {
        if (!this.productArea || !document.body.contains(this.productArea)) {
          this.productArea = document.querySelector(this.dataset.productAreaSelector);
        }

        if (!this.productArea) {
          this.setVisible(false);
          return;
        }

        const productBottom = this.productArea.getBoundingClientRect().bottom;
        this.setVisible(productBottom <= window.innerHeight - 200);
      }

      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.toggleAttribute('inert', !visible);
        document.body.classList.toggle('carson-sticky-atc-visible', visible);
      }

      handleChange(event) {
        if (!event.target.matches('.carson-sticky-atc__select')) return;

        const selectedOption = event.target.selectedOptions[0];
        if (!selectedOption) return;

        this.updateFromOption(selectedOption);
        this.syncMainVariantPicker(selectedOption);
      }

      updateFromOption(option) {
        const variantIdInput = this.querySelector('input[name="id"]');
        const quantityInput = this.querySelector('.carson-sticky-atc__quantity');
        const price = this.querySelector('.carson-sticky-atc__price');
        const image = this.querySelector('.carson-sticky-atc__image');
        const submitButton = this.querySelector('.carson-sticky-atc__button');
        const submitText = submitButton?.querySelector('span');
        const isAvailable = option.dataset.available === 'true';

        if (variantIdInput) {
          variantIdInput.value = option.value;
          variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (quantityInput && option.dataset.quantityMin) {
          quantityInput.value = option.dataset.quantityMin;
        }

        if (price && option.dataset.price) {
          price.innerHTML = this.renderPrice(option);
        }

        if (image && option.dataset.image) {
          image.src = option.dataset.image;
          image.removeAttribute('srcset');
        }

        if (submitButton) {
          submitButton.toggleAttribute('disabled', !isAvailable);
          submitButton.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
        }

        if (submitText) {
          submitText.textContent = isAvailable
            ? window.variantStrings?.addToCart || 'Add to cart'
            : window.variantStrings?.soldOut || 'Sold out';
        }
      }

      renderPrice(option) {
        if (!option.dataset.compareAtPrice) {
          return `<span class="carson-sticky-atc__price-current">${option.dataset.price}</span>`;
        }

        return `
          <span class="visually-hidden">${window.variantStrings?.salePrice || 'Sale price'}</span>
          <span class="carson-sticky-atc__price-current carson-sticky-atc__price-current--sale">${option.dataset.price}</span>
          <span class="visually-hidden">${window.variantStrings?.regularPrice || 'Regular price'}</span>
          <s class="carson-sticky-atc__price-compare">${option.dataset.compareAtPrice}</s>
        `;
      }

      syncMainVariantPicker(option) {
        const variantSelects = this.productInfo?.querySelector('variant-selects');
        const selectedOptions = this.parseSelectedOptions(option);

        if (!variantSelects || !selectedOptions.length) return;

        let changeTarget = null;

        selectedOptions.forEach((value, index) => {
          const group =
            variantSelects.querySelector(`#Option-${this.dataset.sectionId}-${index}`) ||
            variantSelects.querySelector(`fieldset:nth-of-type(${index + 1})`);

          if (!group) return;

          if (group.matches('select')) {
            Array.from(group.options).forEach((selectOption) => {
              const isSelected = selectOption.value === value;
              selectOption.selected = isSelected;
              selectOption.toggleAttribute('selected', isSelected);
            });
            changeTarget = group;
            return;
          }

          const input = group.querySelector(`input[value="${CSS.escape(value)}"]`);
          if (input) {
            input.checked = true;
            changeTarget = input;
          }
        });

        changeTarget?.dispatchEvent(new Event('change', { bubbles: true }));
      }

      parseSelectedOptions(option) {
        try {
          return JSON.parse(option.dataset.options || '[]');
        } catch {
          return [];
        }
      }

      handleVariantChange(event) {
        if (event?.data?.sectionId !== this.dataset.sectionId) return;

        const source = event.data.html?.querySelector(`#${CSS.escape(this.id)}`);
        if (!source) return;

        this.innerHTML = source.innerHTML;
        this.handleResize();
      }
    }
  );
}
